import { useEffect, useState } from "react";
import api, { dkk, num } from "@/lib/api";
import { CreditCard, Banknote, Smartphone } from "lucide-react";

const COLORS = {
  Visa: "#38BDF8",
  Mastercard: "#FB923C",
  MobilePay: "#34D399",
  Kontant: "#A78BFA",
  "C 1ST Pay": "#F472B6",
  Vipps: "#FBBF24",
  Andet: "#64748B",
};
const colorFor = (name, i) => COLORS[name] || ["#38BDF8", "#FB923C", "#34D399", "#A78BFA", "#F472B6", "#FBBF24", "#22D3EE", "#94A3B8"][i % 8];

export function PaymentMethods({ store, dateParams }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dateParams) return;
    let active = true;
    setLoading(true);
    api.get("/kpi/payment-methods", { params: { ...dateParams, store } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [store, dateParams]);

  if (loading) {
    return <p className="text-slate-500 text-sm py-8 text-center" data-testid="payment-loading">Henter…</p>;
  }
  if (!data || !data.methods?.length) {
    return (
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="payment-methods">
        <p className="text-slate-500 text-sm py-8 text-center" data-testid="payment-empty">Ingen betalingsdata for perioden endnu.</p>
      </div>
    );
  }

  const total = data.total || 1;
  const cashPct = Math.round((data.cash / total) * 100);
  const elPct = 100 - cashPct;
  const partial = data.covered_from && dateParams?.from && data.covered_from > dateParams.from;

  return (
    <div className="space-y-4" data-testid="payment-methods">
      {partial && (
        <div className="rounded-xl bg-[#fef6e7] border border-[#fcd34d] text-amber-600 text-sm px-4 py-2.5" data-testid="payment-partial-note">
          Betalingsdata dækker fra og med {data.covered_from}. Ældre dage i perioden er ikke synkroniseret endnu.
        </div>
      )}
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="payment-total">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Omsætning i alt</div>
          <div className="font-heading text-3xl font-bold tabular-nums text-slate-900">{dkk(data.total)}</div>
          <div className="text-xs text-slate-500 mt-1">{num(data.total_orders)} betalinger</div>
        </div>
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="payment-electronic">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Kort & mobil</div>
          <div className="font-heading text-3xl font-bold tabular-nums" style={{ color: "#38BDF8" }}>{dkk(data.electronic)}</div>
          <div className="text-xs text-slate-500 mt-1">{elPct}% af omsætningen</div>
        </div>
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="payment-cash">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-2"><Banknote className="w-4 h-4" /> Kontant</div>
          <div className="font-heading text-3xl font-bold tabular-nums" style={{ color: "#A78BFA" }}>{dkk(data.cash)}</div>
          <div className="text-xs text-slate-500 mt-1">{cashPct}% af omsætningen</div>
        </div>
      </div>

      {/* Kort vs kontant bar */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5">
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Kort/mobil vs. kontant</div>
        <div className="h-4 rounded-full bg-[#e2e8f0] overflow-hidden flex">
          <div className="h-full" style={{ width: `${elPct}%`, background: "#38BDF8" }} />
          <div className="h-full" style={{ width: `${cashPct}%`, background: "#A78BFA" }} />
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#38BDF8" }} /> Kort & mobil {elPct}%</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#A78BFA" }} /> Kontant {cashPct}%</span>
        </div>
      </div>

      {/* Methods breakdown */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="payment-breakdown">
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4 flex items-center gap-2"><Smartphone className="w-4 h-4" /> Fordeling pr. betalingsmiddel</div>
        <ul className="space-y-3.5">
          {data.methods.map((m, i) => (
            <li key={`${m.name}-${i}`} data-testid={`payment-method-${i}`}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="flex items-center gap-2 text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(m.name, i) }} />{m.name}
                </span>
                <span className="tabular-nums text-slate-800"><span className="text-slate-500 mr-3">{m.pct > 0 && m.pct < 0.1 ? "<0,1%" : `${m.pct}%`}</span>{dkk(m.amount)}</span>
              </div>
              <div className="h-2 rounded bg-[#e2e8f0] overflow-hidden">
                <div className="h-full rounded" style={{ width: `${Math.max(0, m.pct)}%`, background: colorFor(m.name, i) }} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
